/**
 * The addresses from before the R-5 cutover, and the address each one is now (F-0250, AC-AD4 … AC-AD6).
 *
 * ⭐ **Why these are replaced rather than dropped.** R-6 ruled out redirects on the premise that
 * nothing had gone live. That stopped being true of `/p/`: pushes and MCP results carried it, the
 * iPhone app builds `/p/<key>~<slug>/items/<KEY>` itself, and desktop tabs were saved with it and with
 * the old root app routes. Every segment involved is reserved (`p`, `settings`, `items` …), so no
 * stranger can ever answer one of these addresses — the capture R-6 feared cannot happen here. A
 * transfer or a rename still leaves nothing behind (ADR-111); this is the one-time cutover only.
 */
import {
  appPath,
  LEGACY_APP_SEGMENTS,
  LEGACY_WORK_AREA_SEGMENT,
  workAreaRoutePath,
} from './paths.js';

/** The separator of the interim route reference, `acme~platform~api` (AC-M19). Read only here. */
export const LEGACY_ROUTE_REF_SEPARATOR = '~';

/** `acme~platform~api` → `acme/platform/api`. A reference already in the `/` form is unchanged. */
export function referenceOfLegacyRouteRef(routeRef: string): string {
  return routeRef.split(LEGACY_ROUTE_REF_SEPARATOR).join('/');
}

/** What an old address becomes. */
export type LegacyAddressTargetType =
  /** Known without asking anyone: the new pathname. */
  | { readonly kind: 'address'; readonly pathname: string }
  /**
   * `/p/tracker/items`: a bare slug, which only the reader's work areas can resolve (AC-M18). `route`
   * is what followed it, `/items`, to be kept on whichever work area it turns out to mean.
   */
  | { readonly kind: 'bare-work-area'; readonly slug: string; readonly route: string };

/** The one old app address whose path carried a work-area reference: a decision in All work areas. */
const WORK_AREAS_DECISION = /^\/work-areas\/decisions\/([^/]+)\/([^/]+)\/?$/;

/**
 * The address an old pathname is now, or `null` when it is not an old address.
 *
 * - `/p/acme~platform~api/items/F-1` → `/acme/platform/api/-/items/F-1`, the key form keeping its key;
 * - `/p/tracker/items` → a bare slug to resolve;
 * - `/p` → `/-/work-areas`;
 * - `/settings`, `/items/F-1` … → `/-/settings`, `/-/items/F-1`;
 * - `/work-areas/decisions/acme~tracker/D-3` → `/-/work-areas/decisions/acme/tracker/D-3`.
 *
 * The query and the fragment are the caller's to keep: this reads a pathname.
 */
export function legacyAddressTarget(pathname: string): LegacyAddressTargetType | null {
  const segments = pathname.split('/').filter((segment) => segment !== '');
  const [first, ...rest] = segments;
  if (first === undefined) return null;

  if (first === LEGACY_WORK_AREA_SEGMENT) {
    const [routeRef, ...route] = rest;
    if (!routeRef) return { kind: 'address', pathname: appPath('/work-areas') };
    const reference = referenceOfLegacyRouteRef(decodeURIComponent(routeRef));
    const tail = route.length > 0 ? `/${route.join('/')}` : '';
    if (!reference.includes('/')) {
      return { kind: 'bare-work-area', slug: reference, route: tail };
    }
    return { kind: 'address', pathname: workAreaRoutePath(reference, tail) };
  }

  if (!LEGACY_APP_SEGMENTS.includes(first)) return null;
  const path = `/${segments.join('/')}`;
  const decision = WORK_AREAS_DECISION.exec(path);
  if (decision) {
    const reference = referenceOfLegacyRouteRef(decodeURIComponent(decision[1]));
    // A bare slug from before I-0656 names no work area on its own here, and the list is one tap
    // from the decision: better than a guess.
    if (!reference.includes('/'))
      return { kind: 'address', pathname: appPath('/work-areas/decisions') };
    return {
      kind: 'address',
      pathname: appPath(`/work-areas/decisions/${reference}/${decision[2]}`),
    };
  }
  return { kind: 'address', pathname: appPath(path) };
}
